import React, { useState } from "react";
import {
  ShieldCheck,
  Users,
  FlaskConical,
  Clock,
  FileCheck,
  Check,
  Lock,
  ArrowRight,
} from "lucide-react";
import "./FamilyPlanning.css";


const STAGES = ["CONSENT", "ORDER", "PROCESSING", "RESULT"];

const RESULT_META = {
  NO_OVERLAP: {
    tone: "calm",
    label: "No overlapping carrier risk found",
    note: "Based on the panel tested, no shared carrier risk was identified. A clear result reduces risk but does not eliminate it — no panel identifies every inherited condition.",
    showReferral: false,
  },
  REVIEW: {
    tone: "review",
    label: "Pending professional review",
    note: "A finding in your results needs review by a genetics professional before it can be fully explained. This is a routine step, not a diagnosis.",
    showReferral: true,
  },
  AT_RISK_COUPLE: {
    tone: "risk",
    label: "Shared carrier risk identified",
    note: "Both partners carry a variant linked to the same inherited condition. A genetic counselor can walk you through what this means for family planning.",
    showReferral: true,
  },
  X_LINKED_REVIEW: {
    tone: "risk",
    label: "X-linked finding — review needed",
    note: "An X-linked result was identified that may be relevant to family planning. A genetic counselor can explain the specific implications.",
    showReferral: true,
  },
  INSUFFICIENT: {
    tone: "neutral",
    label: "Test incomplete or failed",
    note: "The lab couldn't return a conclusive result from this sample. No inference is drawn — a retest can usually be arranged at no extra cost.",
    showReferral: false,
  },
};

export default function FamilyPlanningDesign() {
  const [stageIndex, setStageIndex] = useState(0);
  const [consentA, setConsentA] = useState(false);
  const [consentB, setConsentB] = useState(false);
  const [resultState, setResultState] = useState("AT_RISK_COUPLE");

  const stage = STAGES[stageIndex];
  const bothConsented = consentA && consentB;
  const result = RESULT_META[resultState];

  const railStatus = (index) => {
    if (index < stageIndex) return "is-done";
    if (index === stageIndex) return "is-current";
    return "is-upcoming";
  };

  return (
    <div className="fp-design">
      {/* ---------- dev-only preview controls ---------- */}
      <div className="fp-preview-bar">
        <p className="fp-preview-label">Design preview — remove in production</p>
        <div className="fp-preview-row">
          {STAGES.map((s, i) => (
            <button
              key={s}
              type="button"
              className="fp-chip"
              aria-pressed={stage === s}
              onClick={() => setStageIndex(i)}
            >
              {s}
            </button>
          ))}
        </div>
        {stage === "RESULT" && (
          <div className="fp-preview-row">
            {Object.keys(RESULT_META).map((key) => (
              <button
                key={key}
                type="button"
                className="fp-chip"
                aria-pressed={resultState === key}
                onClick={() => setResultState(key)}
              >
                {key}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ---------- eyebrow ---------- */}
      <div className="fp-eyebrow">
        <span className="fp-eyebrow-text">FutureSoulmates · Family planning</span>
        <span className="fp-badge">
          <Lock size={13} aria-hidden="true" />
          Separate clinical service
        </span>
      </div>

      {/* ---------- hero ---------- */}
      <div className="fp-hero">
        <h1>A private check-up, kept apart from your match.</h1>
        <p>
          Optional carrier screening for couples planning a family, run through an
          accredited clinical lab. Nothing here is read by our matching system.
        </p>
        <p className="fp-hero-rule">
          Rule: family planning results never affect your compatibility score,
          matches, or rankings.
        </p>
      </div>

      {/* ---------- stage rail + content ---------- */}
      <div className="fp-layout">
        <div className="fp-rail">
          {[
            { key: "CONSENT", icon: ShieldCheck, title: "Consent", sub: "Both partners agree" },
            { key: "ORDER", icon: FlaskConical, title: "Order testing", sub: "Sent to lab partner" },
            { key: "PROCESSING", icon: Clock, title: "Lab processing", sub: "1–3 weeks typical" },
            { key: "RESULT", icon: FileCheck, title: "Result", sub: "Reviewed and shared" },
          ].map((item, i) => {
            const status = railStatus(i);
            const Icon = item.icon;
            return (
              <div className={`fp-rail-item is-${status.replace("is-", "")}`} key={item.key}>
                {i < 3 && <div className={`fp-rail-connector ${i < stageIndex ? "is-filled" : ""}`} />}
                <div className={`fp-rail-dot ${status}`}>
                  {status === "is-done" ? <Check size={13} /> : <Icon size={13} />}
                </div>
                <div className="fp-rail-text">
                  <p className="fp-rail-title">{item.title}</p>
                  <p className="fp-rail-sub">{item.sub}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="fp-panel">
          {stage === "CONSENT" && (
            <>
              <h2>Clinical consent</h2>
              <p className="fp-panel-copy">
                Both partners consent separately before we order testing through
                an accredited clinical laboratory.
              </p>

              <div className="fp-seals">
                <div className="fp-seal-col">
                  <div className={`fp-seal ${consentA ? "is-consented" : ""}`}>A</div>
                  <span className="fp-seal-name">You</span>
                  <span className={`fp-seal-status ${consentA ? "is-consented" : ""}`}>
                    {consentA ? "Consented" : "Not yet"}
                  </span>
                  <button
                    type="button"
                    className="fp-consent-btn"
                    disabled={consentA}
                    onClick={() => setConsentA(true)}
                  >
                    {consentA ? "Consent given" : "Give consent"}
                  </button>
                </div>

                <div className={`fp-seal-link ${bothConsented ? "is-closed" : ""}`} />

                <div className="fp-seal-col">
                  <div className={`fp-seal ${consentB ? "is-consented" : ""}`}>T</div>
                  <span className="fp-seal-name">Tarun</span>
                  <span className={`fp-seal-status ${consentB ? "is-consented" : ""}`}>
                    {consentB ? "Consented" : "Waiting"}
                  </span>
                  <button
                    type="button"
                    className="fp-consent-btn"
                    disabled={consentB}
                    onClick={() => setConsentB(true)}
                  >
                    {consentB ? "Consent given" : "Give consent"}
                  </button>
                </div>
              </div>

              {bothConsented && (
                <button type="button" className="fp-btn-primary" onClick={() => setStageIndex(1)}>
                  Continue to ordering
                  <ArrowRight size={15} aria-hidden="true" />
                </button>
              )}
            </>
          )}

          {stage === "ORDER" && (
            <>
              <h2>Order testing</h2>
              <p className="fp-panel-copy">
                Your order routes to our accredited lab partner. Results come back
                directly from the lab — nothing here interprets raw genetic data.
              </p>
              <div className="fp-order-meta">
                <div>Panel <span>Expanded carrier screen</span></div>
                <div>Lab partner <span>Configured per region</span></div>
                <div>Turnaround <span>1–3 weeks typical</span></div>
              </div>
              <button type="button" className="fp-btn-primary" onClick={() => setStageIndex(2)}>
                Order testing
                <ArrowRight size={15} aria-hidden="true" />
              </button>
            </>
          )}

          {stage === "PROCESSING" && (
            <>
              <h2>Awaiting lab result</h2>
              <p className="fp-panel-copy">
                Your order has been placed. This can take one to several weeks —
                we'll notify you both the moment a result is ready.
              </p>
              <div className="fp-order-meta">
                <div>Order ID <span>LAB-20260825-3391</span></div>
                <div>Placed <span>Aug 25, 2026</span></div>
              </div>
              <div className="fp-pulse-row">
                <span className="fp-pulse-dot" aria-hidden="true" />
                Waiting on lab partner
              </div>
            </>
          )}

          {stage === "RESULT" && (
            <>
              <h2>Result</h2>
              <p className="fp-panel-copy">
                This report is clinical information only. It has no effect on your
                compatibility score or matches.
              </p>
              <div className={`fp-result-card state-${result.tone}`}>
                <span className={`fp-result-label state-${result.tone}`}>
                  {resultState.replace(/_/g, " ")}
                </span>
                <h3 className="fp-result-title">{result.label}</h3>
                <p className="fp-result-note">{result.note}</p>
                {result.showReferral && (
                  <button type="button" className="fp-btn-primary">
                    <Users size={15} aria-hidden="true" />
                    Talk to a genetic counselor
                  </button>
                )}
                <div className="fp-result-footer">
                  <span>Report ref: FPGR-9284-B</span>
                  <span>Received Aug 25, 2026</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <p className="fp-footer">
        Your family planning data is stored separately and is never shared with
        the matching or ranking system.
      </p>
    </div>
  );
}